import React from 'react'
import { listPublicPlans, listActivePlansIncludingInvisible } from '@/modules/plans/services/memberPlanService'
import type { Plan } from '@/modules/plans/types'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Button } from '@/shared/ui'
import { setSelectedPlan } from '@/modules/checkout/utils/selection'
import { Label } from '@/shared/ui'
import { Switch } from '@/shared/ui'
import { getExclusiveAllowedPlanIds, getExclusivePopularPlanId } from '@/modules/settings/services/settingsService'
import { ensureExclusiveSessionFromUrl, isExclusiveSessionActive } from '@/modules/settings/utils/exclusiveLink'
import PlanAssetsModal from '@/modules/plans/components/PlanAssetsModal'
import { usePlanAssetCount } from '@/modules/plans/hooks/usePlanAssetCount'

const FREEMIUM_PLAN_IDS = ['trial_7d']
const FREEMIUM_PLAN_KEYWORDS = ['freemium']

function formatPrice(amountCents: number, currency?: string) {
  const value = (amountCents || 0) / 100
  const code = (currency || 'BRL').toUpperCase()
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(value)
  } catch {
    return `R$ ${value.toFixed(2)}`
  }
}

function FreemiumHighlightCard({
  plan,
  onSelect,
  assetCount,
}: {
  plan: Plan
  onSelect: () => void
  assetCount: number | null
}) {
  const countLabel = typeof assetCount === 'number' ? String(assetCount) : '...'
  const description = `Use o Freemium com ${countLabel} moedas monitoradas e descubra como o sistema funciona de verdade.\nQuando sentir o impacto, libere todos os recursos com um plano pago.\n\n\u{1F680} Comece no Freemium agora. Sem risco. Sem compromisso.`
  return (
    <motion.div
      initial={{ opacity: 0.96, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, type: 'spring', stiffness: 120, damping: 20 }}
      className="relative overflow-hidden rounded-2xl border border-amber-500/60 bg-gradient-to-r from-amber-900/80 via-purple-900/75 to-emerald-900/80 p-6 text-amber-50 shadow-lg shadow-black/30 md:p-8"
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-amber-700/30 via-transparent to-emerald-600/25" />
      <div className="absolute right-4 top-4 rounded-full bg-lime-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-black shadow-sm shadow-black/30">
        Popular
      </div>
      <div className="relative grid items-center gap-6 md:grid-cols-2">
        <div className="flex flex-col items-center justify-center space-y-3 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.08em] text-amber-100/90">Plano Freemium</p>
          <div className="flex flex-col items-center gap-1">
            <span className="text-5xl font-black leading-tight text-amber-50 md:text-6xl">
              {formatPrice(plan.amount_cents || 0, plan.currency)}
            </span>
            <span className="text-sm font-semibold text-amber-100/80">/ mês</span>
          </div>
        </div>
        <div className="space-y-4 text-left text-amber-50">
          <div className="space-y-2">
            <p className="text-lg font-semibold sm:text-xl">
              Comece grátis e veja o poder do sistema na prática.
            </p>
            <p className="text-sm font-medium sm:text-base whitespace-pre-line">
              {description}
            </p>
          </div>
          <Button
            variant="default"
            className="w-full"
            onClick={onSelect}
          >
            Ativar Freemium
          </Button>
        </div>
      </div>
    </motion.div>
  )
}

function normalizeText(value?: string | null) {
  return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

function isFreemiumPlan(plan: Plan): boolean {
  const id = String(plan.id || '').toLowerCase()
  const slug = String(plan.slug || '').toLowerCase()
  const name = normalizeText(plan.name)
  if (FREEMIUM_PLAN_IDS.some((key) => key === id || key === slug)) return true
  return FREEMIUM_PLAN_KEYWORDS.some((text) => name.includes(text))
}

export default function PlansHomeSection() {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [annual, setAnnual] = React.useState(false)
  const [exclusive, setExclusive] = React.useState(false)
  const [allowed, setAllowed] = React.useState<string[]>([])
  const [exPopular, setExPopular] = React.useState<string | null>(null)
  const [assetsPlan, setAssetsPlan] = React.useState<Plan | null>(null)

  React.useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const ok = await ensureExclusiveSessionFromUrl()
        const ex = ok || isExclusiveSessionActive()
        setExclusive(ex)
        const [list, ids, pop] = await Promise.all([
          ex ? listActivePlansIncludingInvisible() : listPublicPlans(),
          ex ? getExclusiveAllowedPlanIds() : Promise.resolve([] as string[]),
          ex ? getExclusivePopularPlanId() : Promise.resolve(null),
        ])
        setPlans(list)
        setAllowed(ids)
        setExPopular(pop)
      } catch (e: any) {
        setError(e?.message || 'Falha ao carregar planos')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const displayPlans = React.useMemo(() => (
    exclusive ? plans.filter(p => p.active && (p.visible || allowed.includes(p.id))) : plans
  ), [plans, exclusive, allowed])
  const sortedPlans = React.useMemo(() => (
    [...displayPlans].sort((a,b)=> (a.meta?.home_position ?? 999) - (b.meta?.home_position ?? 999))
  ), [displayPlans])
  const freemiumPlan = React.useMemo(() => sortedPlans.find(isFreemiumPlan) || null, [sortedPlans])
  const freemiumPlanActive = React.useMemo(() => !!freemiumPlan && freemiumPlan.active && freemiumPlan.visible, [freemiumPlan])
  const { count: freemiumAssetCount } = usePlanAssetCount(freemiumPlan?.id)
  const plansWithoutFreemium = React.useMemo(
    () => (freemiumPlanActive ? sortedPlans.filter((p) => !isFreemiumPlan(p)) : sortedPlans),
    [sortedPlans, freemiumPlanActive]
  )
  // Cobran��a anual: habilitar apenas quando houver desconto anual configurado em pelo menos um plano
  const annualEnabled = React.useMemo(() => (
    plansWithoutFreemium.some((p) => {
      try { return getAnnualDiscountPercent(p) > 0 } catch { return false }
    })
  ), [plansWithoutFreemium])
  const hasAnnualPlan = annualEnabled

  React.useEffect(() => {
    if (!annualEnabled && annual) setAnnual(false)
  }, [annualEnabled, annual])

  const defaultPopularId = React.useMemo(() => plansWithoutFreemium.find(p=> p.meta?.popular)?.id ?? null, [plansWithoutFreemium])
  const popularId = React.useMemo(() => (
    freemiumPlanActive && freemiumPlan ? freemiumPlan.id : (exclusive ? (exPopular || null) : defaultPopularId)
  ), [freemiumPlanActive, freemiumPlan, exclusive, exPopular, defaultPopularId])

  // Cobrança anual: desativar quando não houver desconto anual em nenhum plano
  const anyAnnualDiscount = annualEnabled
  React.useEffect(() => {
    if (!anyAnnualDiscount && annual) setAnnual(false)
  }, [anyAnnualDiscount, annual])
  const maxAnnualDiscount = React.useMemo(
    () => Math.max(0, ...plansWithoutFreemium.map((p) => Number((p.meta as any)?.annual_discount_percent || 0))),
    [plansWithoutFreemium]
  )

  function monthlyBaseCents(p: Plan) {
    if (p.interval === 'year') return Math.round(p.amount_cents / 12)
    return p.amount_cents
  }

  function getAnnualDiscountPercent(p: Plan): number {
    const raw = (p.meta as any)?.annual_discount_percent
    const n = Number(raw)
    return Number.isFinite(n) ? Math.max(0, Math.min(99, n)) : 0
  }

  function getMonthlyDiscountPercent(p: Plan): number {
    // Desconto configurado no próprio plano mensal (features.discount_percent)
    if (p.interval !== 'month') return 0
    const raw = (p.meta as any)?.discount_percent ?? (p.meta as any)?.discount?.percent
    const n = Number(raw)
    return Number.isFinite(n) ? Math.max(0, Math.min(99, n)) : 0
  }

  function displayedMonthlyCents(p: Plan): number {
    const base = monthlyBaseCents(p)
    if (annual) {
      const pct = getAnnualDiscountPercent(p)
      if (pct > 0) return Math.floor(base * (1 - pct / 100))
      return base
    }
    // Modo mensal: aplicar desconto do plano mensal, quando existir
    const mPct = getMonthlyDiscountPercent(p)
    if (mPct > 0) return Math.floor(base * (1 - mPct / 100))
    return base
  }

  return (
    <section id="pricing" className="py-20">
      <PlanAssetsModal open={!!assetsPlan} plan={assetsPlan} onClose={() => setAssetsPlan(null)} />
      <div className="mx-auto max-w-[1550px] px-6">
        <div className="mb-12 space-y-4 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Planos de Assinatura</h2>
          <p className="text-lg text-muted-foreground whitespace-pre-line">
            {`Escolha o plano que funciona para você\nTodos incluem acesso à plataforma e suporte.`}
          </p>
        </div>

        {loading && <div className="text-center text-sm text-foreground/70">Carregando planos…</div>}
        {error && (
          <div className="mx-auto max-w-xl rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-foreground">{error}</div>
        )}

        {!loading && !error && (
          <>
            {freemiumPlanActive && freemiumPlan && (
              <div className="mb-8">
                <FreemiumHighlightCard
                  plan={freemiumPlan}
                  assetCount={freemiumAssetCount}
                  onSelect={() => {
                    setSelectedPlan({ plan: (freemiumPlan.id || freemiumPlan.slug), interval: 'month' })
                    window.location.href = '/cadastro?plan=trial_7d'
                  }}
                />
              </div>
            )}
            <div className="mb-6 flex items-center justify-center gap-2" style={{ display: hasAnnualPlan ? undefined : "none" }}>
              <Label className="text-sm">Cobrança anual</Label>
              <Switch disabled={!anyAnnualDiscount} checked={annual} onChange={(e)=> setAnnual((e.target as HTMLInputElement).checked)} />
              {annual && <span className="text-xs text-primary ml-1">(Economize ate {maxAnnualDiscount}%)</span>}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {plansWithoutFreemium.map((p) => {
              const isPopular = p.id === popularId
              const baseMonthly = monthlyBaseCents(p)
              const shownMonthly = displayedMonthlyCents(p)
              const appliedAnnualPct = annual ? getAnnualDiscountPercent(p) : 0
              const appliedMonthlyPct = !annual ? getMonthlyDiscountPercent(p) : 0
              const appliedPct = appliedAnnualPct || appliedMonthlyPct
              const hasDiscount = appliedPct > 0 && shownMonthly < baseMonthly
              const monthlyRecurrence = String(((p.meta as any)?.discount_recurrence ?? (p.meta as any)?.discount?.recurrence) || 'recurring').toLowerCase()
              const meta: any = p.meta || {}
              const assetsSummary = String(meta.assets_summary || '').trim()
              const assetsLinkLabel = String(meta.assets_link_label || '').trim() || 'Clique e veja as moedas do plano'
              const showAssetsRow = !!assetsSummary || !!meta.assets_link_label
              return (
                <motion.div
                  key={p.id}
                  initial={{ y: 30, opacity: 0.95 }}
                  whileInView={{ y: isPopular ? -10 : 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, type: 'spring', stiffness: 100, damping: 26, delay: 0.2 }}
                  className={cn(
                    'relative flex flex-col rounded-2xl border p-6 text-center lg:justify-center bg-background',
                    isPopular ? 'border-primary' : 'border-border',
                    !isPopular && 'md:mt-4'
                  )}
                >
                  {hasDiscount && (
                    <div className="absolute left-0 top-0 rounded-br-xl rounded-tl-xl bg-primary px-2 py-0.5 text-primary-foreground">
                      -{appliedPct}%
                    </div>
                  )}
                  {isPopular && (
                    <div className="absolute right-0 top-0 rounded-bl-xl rounded-tr-xl bg-primary px-2 py-0.5 text-primary-foreground">
                      Popular
                    </div>
                  )}
                  <div className="flex flex-1 flex-col">
                    <p className="text-base font-semibold text-muted-foreground">{p.name}</p>
                    <div className="mt-6 flex items-center justify-center gap-2">
                      <span className="text-5xl font-bold tracking-tight text-foreground">{formatPrice(shownMonthly, p.currency)}</span>
                      <span className="text-sm font-semibold leading-6 tracking-wide text-muted-foreground">/ mês</span>
                    </div>
                    {hasDiscount && (
                      <div className="mt-1 space-y-0.5">
                        <div className="text-xs text-foreground/60 line-through">{formatPrice(baseMonthly, p.currency)} / mês</div>
                        <div className="text-xs text-primary">
                          Economize {appliedPct}% {(!annual && monthlyRecurrence === 'one_time') ? '(1ª cobrança)' : ''}
                        </div>
                      </div>
                    )}
                    {p.description && <p className="mt-2 text-xs text-muted-foreground">{p.description}</p>}
                    {p.features?.length > 0 && (
                      <ul className="mt-5 flex flex-col gap-2 text-left">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {showAssetsRow && (
                      <div className="mt-3 flex items-start gap-2 text-left text-sm text-foreground/80">
                        <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                        <div>
                          {assetsSummary && <span>{assetsSummary} </span>}
                          <button
                            type="button"
                            className="inline-flex items-center text-sm font-semibold text-primary underline-offset-2 hover:underline"
                            onClick={() => setAssetsPlan(p)}
                          >
                            {assetsLinkLabel}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="mt-4">
                      <Button
                        variant={isPopular ? 'default' : 'outline'}
                        className={cn('w-full')}
                        onClick={() => {
                          setSelectedPlan({ plan: (p.id || p.slug), interval: (annual ? 'year' : 'month') })
                          window.location.href = '/cadastro'
                        }}
                      >
                        Assinar
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )
            })}
            {plansWithoutFreemium.length === 0 && !freemiumPlanActive && (
              <div className="text-center text-sm text-foreground/70 md:col-span-3">Nenhum plano disponível no momento.</div>
            )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}
